<template>
    <Card pageTitle="Price Adjustments">
        <div>
            <AdjustmentTabs />
            <div>
                <div class="d-flex justify-content-between align-items-center">
                    <div class="mb-3"><strong>Price Adjustments</strong></div>
                    <div class="d-flex" style="gap: 10px">
                        <AllowPermission :permission="PERMISSION_CODES.CMS_CREATE_ADJUSTMENT">
                            <router-link to="/inventory-adjustments/price/create">
                                <Button noMargin>Create Price Adjustment</Button>
                            </router-link>
                        </AllowPermission>
                    </div>
                </div>
                <p class="mb-1">Search By ID</p>
                <form @submit.prevent="fetchPriceAdjustments">
                    <div class="d-flex mb-3" style="max-width: 400px; gap: 15px">
                        <CustomInput class="flex-1" small type="text" v-model="search" />
                        <Button :condensed="true" noMargin>Search</Button>
                    </div>
                </form>
            </div>
            <div v-if="loading" class="loading-container">
                <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
                <p class="mt-2">Loading price adjustments...</p>
            </div>
            <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_ADJUSTMENT">
                <div class="adjustments-table-container">
                    <div class="table-responsive">
                        <table class="custom-adjustments-table">
                            <thead>
                                <tr>
                                    <th class="adjustment-id-col">
                                        <i class="fas fa-tag mr-2"></i>Adjustment ID
                                    </th>
                                    <th class="items-col">
                                        <i class="fas fa-box mr-2"></i>Items
                                    </th>
                                    <th class="increase-col">
                                        <i class="fas fa-arrow-up mr-2 text-success"></i>Price Increase
                                    </th>
                                    <th class="decrease-col">
                                        <i class="fas fa-arrow-down mr-2 text-danger"></i>Price Decrease
                                    </th>
                                    <th class="net-change-col">
                                        <i class="fas fa-balance-scale mr-2"></i>Net Change
                                    </th>
                                    <th class="date-col">
                                        <i class="fas fa-calendar mr-2"></i>Date Created
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="adjustment of priceAdjustments" :key="adjustment.id" class="adjustment-row">
                                    <td class="adjustment-id-cell">
                                        <LinkComponent :to="`/inventory-adjustments/price/${adjustment.name}`" class="adjustment-link">
                                            <div class="adjustment-id-content">
                                                <span class="adjustment-name">{{ adjustment.name }}</span>
                                                <span class="adjustment-type">Price Adjustment</span>
                                            </div>
                                        </LinkComponent>
                                    </td>
                                    <td class="items-cell">
                                        <div class="items-badge">
                                            {{ adjustment.totalItems }}
                                            <span class="items-text">{{ adjustment.totalItems === 1 ? 'item' : 'items' }}</span>
                                        </div>
                                    </td>
                                    <td class="increase-cell">
                                        <div class="cost-value positive" v-if="adjustment.priceIncrease > 0">
                                            <span class="currency">$</span>{{ formatCurrency(adjustment.priceIncrease) }}
                                        </div>
                                        <div class="cost-value neutral" v-else>
                                            <span class="currency">$</span>0.00
                                        </div>
                                    </td>
                                    <td class="decrease-cell">
                                        <div class="cost-value negative" v-if="adjustment.priceDecrease > 0">
                                            <span class="currency">$</span>{{ formatCurrency(adjustment.priceDecrease) }}
                                        </div>
                                        <div class="cost-value neutral" v-else>
                                            <span class="currency">$</span>0.00
                                        </div>
                                    </td>
                                    <td class="net-change-cell">
                                        <div class="net-change-container">
                                            <div class="net-value" :class="getNetChangeClass(adjustment.netChange)">
                                                <i :class="getNetChangeIcon(adjustment.netChange)" class="mr-1"></i>
                                                <span class="currency">$</span>{{ formatCurrency(Math.abs(adjustment.netChange)) }}
                                            </div>
                                            <div class="net-label" :class="getNetChangeClass(adjustment.netChange)">
                                                {{ getNetChangeLabel(adjustment.netChange) }}
                                            </div>
                                        </div>
                                    </td>
                                    <td class="date-cell">
                                        <div class="date-container">
                                            <div class="date-value">{{ formatDate(adjustment.formattedCreatedAt || adjustment.createdAt) }}</div>
                                            <div class="time-value">{{ formatTime(adjustment.formattedCreatedAt || adjustment.createdAt) }}</div>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div v-if="!loading && !priceAdjustments.length" class="empty-state">
                        <div class="empty-icon">
                            <i class="fas fa-dollar-sign"></i>
                        </div>
                        <h4>No Price Adjustments Found</h4>
                        <p>Start by creating your first price adjustment to track pricing changes.</p>
                        <AllowPermission :permission="PERMISSION_CODES.CMS_CREATE_ADJUSTMENT">
                            <router-link to="/inventory-adjustments/price/create">
                                <Button class="mt-3">
                                    <i class="fas fa-plus mr-2"></i>Create First Adjustment
                                </Button>
                            </router-link>
                        </AllowPermission>
                    </div>
                </div>
                <Paginator @change="updatePage" :page="page" :totalPages="totalPages" />
            </AllowPermission>
        </div>
    </Card>
</template>

<script>
import { mapState } from "vuex"
import CustomInput from "../../../components/crud/custom-input.vue"
import SelectDropdown from "../../../components/crud/SelectDropdown.vue"
import CustomTable from "../../../components/layout/CustomTable.vue"
import Button from "../../../components/UI/Button.vue"
import Card from "../../../components/UI/Card.vue"
import PriceFetchApi from "../../../utils/fetch/price-fetch-api"
import { PERMISSION_CODES } from "../../../store/permission"
import AllowPermission from "../../../components/layout/AllowPermission.vue"
import LinkComponent from "../../../components/UI/LinkComponent.vue"
import Paginator from "../../../components/other/Paginator.vue"
import { format } from "date-fns"
import AdjustmentTabs from "../../../components/inventory/AdjustmentTabs.vue"

export default {
    data() {
        return {
            search: "",
            page: 1,
            limit: 10,
            totalPages: 0,
            loading: false,
            priceAdjustments: [],
            PERMISSION_CODES,
        }
    },
    components: {
        Paginator,
        AdjustmentTabs,
        LinkComponent,
        AllowPermission,
        Card,
        CustomInput,
        Button,
        SelectDropdown,
        CustomTable
    },
    async mounted() {
        await this.fetchPriceAdjustments()
    },
    methods: {
        async fetchPriceAdjustments() {
            try {
                this.loading = true
                const data = await PriceFetchApi.getPriceAdjustments({
                    page: this.page,
                    limit: this.limit,
                    search: this.search
                })
                
                // Format the created dates if they exist
                this.priceAdjustments = data.result.map(adjustment => ({
                    ...adjustment,
                    formattedCreatedAt: adjustment.createdAt ? 
                        format(new Date(adjustment.createdAt), "MMM d, yyyy h:mm a") : 
                        (adjustment.formattedCreatedAt || '-')
                }))
                
                this.totalPages = data.totalPages
            } catch (err) {
                console.log("Error fetching price adjustments", err)
                this.$toast.error("Failed to fetch price adjustments")
            }
            this.loading = false
        },
        updatePage(e) {
            if (this.totalPages > 0) {
                if (e > 0 && e <= this.totalPages) {
                    this.page = e
                } else {
                    this.$toast.error("Invalid page!")
                }
            }
        },
        formatCurrency(value) {
            if (!value) return '0.00'
            return parseFloat(value).toFixed(2)
        },
        formatDate(dateString) {
            if (!dateString) return '-'
            try {
                const date = new Date(dateString)
                return date.toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric', 
                    year: 'numeric' 
                })
            } catch {
                return dateString.split(' ')[0] || '-'
            }
        },
        formatTime(dateString) {
            if (!dateString) return ''
            try {
                const date = new Date(dateString)
                return date.toLocaleTimeString('en-US', { 
                    hour: 'numeric', 
                    minute: '2-digit',
                    hour12: true 
                })
            } catch {
                const parts = dateString.split(' ')
                return parts.length > 1 ? parts.slice(1).join(' ') : ''
            }
        },
        getNetChangeClass(value) {
            if (value > 0) return 'positive'
            if (value < 0) return 'negative'
            return 'neutral'
        },
        getNetChangeIcon(value) {
            if (value > 0) return 'fas fa-arrow-up'
            if (value < 0) return 'fas fa-arrow-down'
            return 'fas fa-minus'
        },
        getNetChangeLabel(value) {
            if (value > 0) return 'Increase'
            if (value < 0) return 'Decrease'
            return 'No Change'
        },
    },
    watch: {
        page(e) {
            this.fetchPriceAdjustments()
        },
        search(e) {
            this.page = 1
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../../assets/variables.scss";

.price-adjustment-setting {
    display: flex;
    width: 100%;
    align-items: center;
    gap: 15px;
}

.loading-container {
    text-align: center;
    padding: 40px 20px;
    color: rgba($textColor, 0.7);
}

.adjustments-table-container { background: $lightCardBg; border-radius: $cardRadius; box-shadow: $cardShadow; overflow: hidden; margin-bottom: $sectionGap * 2; border: 1px solid $lightBorderColor; }

.custom-adjustments-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;

    thead {
        background: linear-gradient(135deg, $secondaryColor 0%, lighten($secondaryColor, 8%) 100%);
        
        th {
            padding: 16px 12px;
            text-align: left;
            font-weight: 600;
            color: white;
            font-size: 0.85rem;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            border: none;
            position: relative;

            i {
                opacity: 0.8;
                font-size: 0.8rem;
            }

            &.adjustment-id-col {
                min-width: 220px;
            }

            &.items-col {
                min-width: 100px;
                text-align: center;
            }

            &.increase-col,
            &.decrease-col,
            &.net-change-col {
                min-width: 120px;
                text-align: center;
            }

            &.date-col {
                min-width: 140px;
                text-align: center;
            }
        }
    }

    tbody {
        tr.adjustment-row { border-bottom: 1px solid $lightBorderColor; transition: all 0.2s ease; }
        tr.adjustment-row:hover { background-color: $lightHoverColor; }
        tr.adjustment-row:last-child { border-bottom: none; }

        td { padding: $tableCellPaddingY $tableCellPaddingX; vertical-align: middle; border: none; }
    }
}

.adjustment-id-cell {
    .adjustment-link {
        text-decoration: none;
        color: inherit;
        display: block;

        &:hover {
            text-decoration: none;
            color: inherit;
        }
    }

    .adjustment-id-content {
        .adjustment-name {
            display: block;
            font-weight: 600;
            color: $primaryColor;
            font-size: 0.95rem;
            margin-bottom: 2px;
        }

        .adjustment-type {
            display: block;
            font-size: 0.75rem;
            color: $accentColor;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            font-weight: 500;
        }
    }
}

.items-cell {
    text-align: center;

    .items-badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        background: linear-gradient(135deg, $lightHoverColor 0%, lighten($lightHoverColor, 4%) 100%);
        padding: 6px 12px;
        border-radius: 20px;
        font-weight: 600;
        color: $textColor;
        font-size: 0.9rem;

        .items-text {
            font-size: 0.75rem;
            color: rgba($textColor, 0.7);
            font-weight: 400;
        }
    }
}

.increase-cell,
.decrease-cell {
    text-align: center;

    .cost-value {
        font-weight: 600;
        font-size: 0.9rem;
        padding: 4px 8px;
        border-radius: 6px;
        display: inline-block;
        min-width: 80px;

        .currency {
            font-size: 0.8rem;
            opacity: 0.8;
        }

        &.positive {
            color: $successColor;
            background-color: rgba($successColor, 0.1);
        }

        &.negative {
            color: $dangerColor;
            background-color: rgba($dangerColor, 0.1);
        }

        &.neutral {
            color: rgba($textColor, 0.7);
            background-color: $lightHoverColor;
        }
    }
}

.net-change-cell {
    text-align: center;

    .net-change-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 2px;
    }

    .net-value {
        font-weight: 700;
        font-size: 0.95rem;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        padding: 6px 12px;
        border-radius: 8px;
        min-width: 90px;

        .currency {
            font-size: 0.85rem;
        }

        &.positive {
            color: $successColor;
            background: linear-gradient(135deg, rgba($successColor, 0.15) 0%, rgba($successColor, 0.05) 100%);
            border: 1px solid rgba($successColor, 0.25);
        }

        &.negative {
            color: $dangerColor;
            background: linear-gradient(135deg, rgba($dangerColor, 0.15) 0%, rgba($dangerColor, 0.05) 100%);
            border: 1px solid rgba($dangerColor, 0.25);
        }

        &.neutral {
            color: rgba($textColor, 0.7);
            background: $lightHoverColor;
            border: 1px solid $lightBorderColor;
        }

        i {
            font-size: 0.8rem;
        }
    }

    .net-label {
        font-size: 0.7rem;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.5px;

        &.positive {
            color: $successColor;
        }

        &.negative {
            color: $dangerColor;
        }

        &.neutral {
            color: rgba($textColor, 0.7);
        }
    }
}

.date-cell {
    text-align: center;

    .date-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 2px;
    }

    .date-value {
        font-weight: 600;
        color: $primaryColor;
        font-size: 0.85rem;
    }

    .time-value {
        font-size: 0.75rem;
        color: $secondaryColor;
        font-weight: 500;
    }
}

.empty-state {
    text-align: center;
    padding: 60px 20px;
    color: rgba($textColor, 0.7);

    .empty-icon {
        margin-bottom: 20px;

        i {
            font-size: 4rem;
            color: rgba($textColor, 0.2);
        }
    }

    h4 {
        color: $primaryColor;
        margin-bottom: 12px;
        font-weight: 600;
    }

    p {
        margin-bottom: 20px;
        color: rgba($textColor, 0.75);
        max-width: 400px;
        margin-left: auto;
        margin-right: auto;
    }
}

/* Dark mode overrides */
:deep(.dark) .adjustments-table-container {
    background: $darkCardBg;
    border-color: $darkBorderColor;
    box-shadow: 0 4px 10px rgba(0,0,0,0.3);
}

:deep(.dark) .custom-adjustments-table thead {
    background: linear-gradient(135deg, $secondaryColorDark 0%, lighten($secondaryColorDark, 6%) 100%);
}

:deep(.dark) .custom-adjustments-table tbody tr.adjustment-row {
    border-bottom: 1px solid $darkBorderColor;
}

:deep(.dark) .custom-adjustments-table tbody tr.adjustment-row:hover {
    background-color: lighten($darkCardBg, 3%);
    box-shadow: 0 2px 8px rgba(0,0,0,0.35);
}

:deep(.dark) .adjustment-id-content .adjustment-name {
    color: $darkTextColor;
}

:deep(.dark) .adjustment-id-content .adjustment-type {
    color: $accentColorDark;
}

:deep(.dark) .items-badge {
    background: linear-gradient(135deg, lighten($darkCardBg, 4%) 0%, lighten($darkCardBg, 6%) 100%);
    color: $darkTextColor;
}

:deep(.dark) .items-badge .items-text {
    color: rgba($darkTextColor, 0.75);
}

:deep(.dark) .increase-cell .cost-value.positive {
    color: $successColorDark;
    background-color: rgba($successColorDark, 0.15);
}

:deep(.dark) .decrease-cell .cost-value.negative {
    color: $dangerColorDark;
    background-color: rgba($dangerColorDark, 0.15);
}

:deep(.dark) .increase-cell .cost-value.neutral,
:deep(.dark) .decrease-cell .cost-value.neutral {
    color: rgba($darkTextColor, 0.7);
    background-color: lighten($darkCardBg, 4%);
}

:deep(.dark) .net-change-cell .net-value.positive {
    color: $successColorDark;
    background: linear-gradient(135deg, rgba($successColorDark, 0.2) 0%, rgba($successColorDark, 0.08) 100%);
    border-color: rgba($successColorDark, 0.25);
}

:deep(.dark) .net-change-cell .net-value.negative {
    color: $dangerColorDark;
    background: linear-gradient(135deg, rgba($dangerColorDark, 0.2) 0%, rgba($dangerColorDark, 0.08) 100%);
    border-color: rgba($dangerColorDark, 0.25);
}

:deep(.dark) .net-change-cell .net-value.neutral {
    color: rgba($darkTextColor, 0.7);
    background: lighten($darkCardBg, 4%);
    border-color: $darkBorderColor;
}

:deep(.dark) .net-change-cell .net-label.positive { color: $successColorDark; }
:deep(.dark) .net-change-cell .net-label.negative { color: $dangerColorDark; }
:deep(.dark) .net-change-cell .net-label.neutral { color: rgba($darkTextColor, 0.7); }

:deep(.dark) .date-cell .date-value { color: $darkTextColor; }
:deep(.dark) .date-cell .time-value { color: $secondaryColorDark; }

:deep(.dark) .loading-container { color: rgba($darkTextColor, 0.7); }

:deep(.dark) .empty-state {
    color: rgba($darkTextColor, 0.7);
}

:deep(.dark) .empty-state .empty-icon i { color: rgba($darkTextColor, 0.25); }

:deep(.dark) .empty-state h4 { color: $darkTextColor; }


// Responsive design
@media (max-width: 1200px) {
    .custom-adjustments-table {
        font-size: 0.85rem;

        thead th {
            padding: 12px 8px;
            font-size: 0.8rem;
        }

        tbody td {
            padding: 12px 8px;
        }
    }
}

@media (max-width: 768px) {
    .adjustments-table-container {
        margin: 0 -10px 20px -10px;
        border-radius: 0;
    }

    .custom-adjustments-table {
        font-size: 0.8rem;

        thead th {
            padding: 10px 6px;
            font-size: 0.75rem;

            i {
                display: none;
            }
        }

        tbody td {
            padding: 10px 6px;
        }

        .adjustment-id-content {
            .adjustment-name {
                font-size: 0.85rem;
            }

            .adjustment-type {
                font-size: 0.7rem;
            }
        }

        .items-badge {
            padding: 4px 8px;
            font-size: 0.8rem;

            .items-text {
                display: none;
            }
        }

        .net-change-container {
            .net-label {
                display: none;
            }
        }

        .date-container {
            .time-value {
                display: none;
            }
        }
    }
}
</style>
